import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { api } from '@/lib/api';
import { EMPTY_RAIL_PREFERENCES, type RailPreferences } from '@/lib/rail-preferences';

/**
 * Osobiste ustawienia paska ikon (kolejność + ukryte widoki + ukryte szare skróty) zalogowanego
 * użytkownika — zapisywane na serwerze (GET/PUT/DELETE /api/auth/rail-preferences), więc idą za
 * użytkownikiem między urządzeniami. `null` z serwera = układ domyślny.
 */

const railPreferencesQueryKey = (tenantId: string | undefined, userId: string | undefined) =>
  ['rail-preferences', tenantId ?? null, userId ?? null] as const;

function sanitize(value: unknown): RailPreferences {
  if (!value || typeof value !== 'object') return EMPTY_RAIL_PREFERENCES;
  const v = value as Partial<RailPreferences>;
  const codes = (arr: unknown) =>
    Array.isArray(arr) ? arr.filter((c): c is string => typeof c === 'string') : [];
  return { order: codes(v.order), hidden: codes(v.hidden), hiddenTiles: codes(v.hiddenTiles) };
}

/**
 * Kopia OSTATNIEGO układu z serwera w przeglądarce — tylko po to, żeby pasek od pierwszej klatki
 * rysował się po swojemu (bez mignięcia układu domyślnego, zanim serwer odpowie). Źródłem prawdy
 * zostaje serwer: kopia jest wyłącznie `placeholderData` i jest nadpisywana jego odpowiedzią.
 */
function cacheKey(tenantId: string, userId: string): string {
  return `saas_rail_prefs_cache:${tenantId}:${userId}`;
}

function readCache(tenantId: string, userId: string): RailPreferences | undefined {
  try {
    const raw = localStorage.getItem(cacheKey(tenantId, userId));
    return raw ? sanitize(JSON.parse(raw)) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(tenantId: string, userId: string, prefs: RailPreferences): void {
  try {
    localStorage.setItem(cacheKey(tenantId, userId), JSON.stringify(prefs));
  } catch {
    // Brak kopii = najwyżej mignięcie układu domyślnego przy starcie.
  }
}

export function useRailPreferences() {
  const { me } = useTenantAuth();
  const tenantId = me?.tenant?.id;
  const userId = me?.user?.id;
  const queryClient = useQueryClient();
  const queryKey = railPreferencesQueryKey(tenantId, userId);

  const query = useQuery({
    queryKey,
    enabled: Boolean(tenantId && userId),
    queryFn: async () => {
      const res = await api.get<{ config: unknown }>('/auth/rail-preferences');
      const prefs = res.data.config ? sanitize(res.data.config) : EMPTY_RAIL_PREFERENCES;
      writeCache(tenantId!, userId!, prefs);
      return prefs;
    },
    placeholderData: () => (tenantId && userId ? readCache(tenantId, userId) : undefined),
    staleTime: 5 * 60 * 1000,
  });

  // `null` = przywróć domyślne (DELETE — usuwa osobiste ustawienia).
  const mutation = useMutation({
    mutationFn: async (prefs: RailPreferences | null) => {
      if (prefs === null) {
        await api.delete('/auth/rail-preferences');
        return EMPTY_RAIL_PREFERENCES;
      }
      const res = await api.put<{ config: unknown }>('/auth/rail-preferences', { config: prefs });
      return sanitize(res.data.config);
    },
    onSuccess: (saved) => {
      queryClient.setQueryData(queryKey, saved);
      if (tenantId && userId) writeCache(tenantId, userId, saved);
    },
  });

  // Nieudany odczyt BEZ żadnego układu w ręku (brak kopii) — pasek rysuje wtedy układ domyślny
  // (to tylko wygląd, nie może zablokować menu), a ekran ustawień blokuje edycję, żeby Zapisz nie
  // nadpisał prawdziwych ustawień na serwerze układem domyślnym.
  const loadFailed = query.isError && query.data === undefined;

  return {
    preferences: query.data ?? EMPTY_RAIL_PREFERENCES,
    // Kopia z przeglądarki wystarcza pasku, ale ekran ustawień czeka na prawdę z serwera
    // (inaczej odpowiedź serwera mogłaby nadpisać świeżo rozpoczętą edycję).
    isLoading: query.isLoading || query.isPlaceholderData,
    isError: loadFailed,
    retry: query.refetch,
    // Układ znany (z serwera albo z kopii) — pasek dopiero wtedy rysuje skróty do ukrycia, żeby
    // ukryte nie mignęły przy starcie, zanim wiadomo, kto jest zalogowany.
    // `me` jeszcze się ładuje → nie gotowe; zalogowany → gotowe, gdy jest układ (serwer/kopia)
    // albo odczyt się nie udał (wtedy układ domyślny).
    isReady: me ? (tenantId && userId ? query.data !== undefined || loadFailed : true) : false,
    save: mutation.mutateAsync,
    isSaving: mutation.isPending,
  };
}
